import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Subject, distinctUntilChanged, filter, map, merge, of, startWith, switchMap } from 'rxjs';
import { BrandService } from './core/services/brand.service';

/**
 * Translates route `title` values through Transloco before setting `document.title`.
 *
 * Conventions:
 * - A title like `"catalogManager.routeTitles.detail"` (contains a dot, no spaces) is
 *   treated as an i18n key. It is translated after the key's root or lazy scope is
 *   loaded, and re-runs on language change so the browser tab stays in sync.
 * - Anything else is used verbatim (backwards compatible with raw titles).
 * - A route with NO title falls back to the resolved brand's tab text. Nearly
 *   every route in this app is titleless, so this fallback — not the route
 *   title — is what the user actually sees in the tab.
 *
 * Wired in `app.config.ts` via `{ provide: TitleStrategy, useClass: TranslocoTitleStrategy }`.
 */
@Injectable({ providedIn: 'root' })
export class TranslocoTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly transloco = inject(TranslocoService);
  private readonly brand = inject(BrandService);
  private readonly routeTitle$ = new Subject<string | undefined>();

  constructor() {
    super();

    this.routeTitle$
      .pipe(
        switchMap((value) => {
          if (value === undefined) {
            return of(this.defaultTitle());
          }

          if (!this.looksLikeI18nKey(value)) {
            return of(value);
          }

          const scopeLoadedTranslations$ = this.transloco.events$.pipe(
            filter((event) => event.type === 'translationLoadSuccess'),
            map(() => this.transloco.getTranslation(this.transloco.getActiveLang()))
          );

          return merge(this.transloco.selectTranslation(), scopeLoadedTranslations$).pipe(
            map((translation) =>
              typeof translation[value] === 'string' ? this.transloco.translate(value) : undefined
            ),
            filter((translated): translated is string => translated !== undefined),
            startWith(this.defaultTitle()),
            distinctUntilChanged()
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((translated) => {
        // Never blank the tab: an empty value means no brand resolved yet, so
        // the pre-bootstrap title from index.html should stand.
        if (translated) {
          this.title.setTitle(translated);
        }
      });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.routeTitle$.next(this.buildTitle(snapshot));
  }

  private looksLikeI18nKey(value: string): boolean {
    return value.includes('.') && !/\s/.test(value);
  }

  /**
   * Tab text for a route that declares no title: the brand's configured
   * `documentTitle`, else its app name. Read per emission — brand state is
   * resolved in APP_INITIALIZER, before the router's first navigation.
   *
   * Empty when no brand resolved (the app is locked in that case). The
   * subscriber skips empty values rather than blanking the tab, leaving the
   * pre-bootstrap title from `index.html` in place.
   */
  private defaultTitle(): string {
    return this.brand.documentTitle() || this.brand.appName() || '';
  }
}
